<template>
    <section class="flex flex-wrap justify-center my-10">
        <LoadingDialog :visible="loading" />
        <ConfirmationModal v-if="askConfirmation" :title="getConfirmationTitle()" :text="getConfirmationText()"
            @emitResponse="checkConfirmationResponse" />

        <template v-if="Array.isArray(items)">
            
            <div class="m-form-w px-5">
                <p class="font-semibold text-lg">{{ translate('Order details') }} ({{ items.length }} {{ translate('courses') }})</p>
                <div class="flex flex-col align-items-center w-full gap-3 border-bottom-1 surface-border py-3 mb-3 pb-0">
                    <div v-for="(item, index) of items" :key="index"
                        class="w-full md:w-2/3 bg-light-white border border-light-white shadow-lg rounded px-2"
                        :class="handleBorder(index)">
                        <img :src="imageHandler.toBase64Img(item.coursePhoto)" :alt="'course photo'"
                            class="aspect-video mt-2 mx-auto shadow border-surface-200 dark:border-surface-700 rounded" />
                        <p class="text-sm text-left font-bold mb-3 text-xl pr-3">
                            {{ item.title }}
                        </p>
                        <p class="text-sm text-left text-chain text-lg">
                            R${{ item.price }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="mt-10 md:mt-0 m-form-w px-5 bg-iron text-chain rounded-sm shadow-lg pt-3 pb-5">
                <p class="font-bold text-xl border-b border-chain pb-2">{{ translate('order summary', 'ucfirst') }}</p>

                <div class="flex justify-between pb-5 mt-3">
                    <p class="font-semibold">
                        {{ translate('total', 'ucfirst') }} ({{ items.length }} {{ translate('courses') }}):
                    </p>
                    <p class="font-bold text-xl">
                        {{ getTotalAmount() }}
                    </p>
                </div>

                <p class="font-semibold text-sm mt-5">
                    {{ translate('By completing your purchase, you agree to these terms and conditions.') }} 
                    <span class="underline cursor-pointer">{{ translate('terms of use', 'capitalize') }}</span>.
                </p>

                <template v-if="!isButtonDisabled">
                    <Button :label="translate('generate payment link', 'ucfirst')" icon="pi pi-link" class="bg-button mt-10 w-full"
                        :disabled="isButtonDisabled" @click.prevent="openConfirmationModal()" />
                    <small class="mt-3">
                        {{ translate('By clicking the button above, a payment link will be generated and you will be redirected.') }}
                    </small>
                </template>
                <template v-else>
                    <Button :label="translate('view pending payments', 'ucfirst')" icon="pi pi-receipt" class="no-bg-button mt-10 w-full"
                        @click.prevent="goToPaymentsView()" />

                    <template v-if="paymentLinkButton">
                        <Button :label="translate('access payment link', 'ucfirst')" icon="pi pi-dollar" class="no-bg-button mt-2 w-full"
                            @click.prevent="urlHandler.openExternal(paymentLinkButton)" />
                        <small class="mt-3">
                            {{ translate('You already have a payment link for this/these course(s).') }}
                        </small>
                    </template>
                    <template v-else>
                        <Button :label="translate('contact support', 'ucfirst')" icon="pi pi-phone" class="bg-purple-suit-forced mt-2 w-full"
                            @click.prevent="urlHandler.redirectAndScroll('/dashboard/support')" />
                    </template>
                </template>

                
                <Button v-if="showSupportButton" :label="translate('contact support', 'ucfirst')" icon="pi pi-phone" class="bg-purple-suit-forced mt-2 w-full"
                    @click.prevent="urlHandler.redirectAndScroll('/dashboard/support')" />
            </div>
        
        </template>

    </section>
</template>
<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import router from "@/router";
import { Button } from 'primevue';
import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
import PaymentService from '@/services/PaymentService';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue';
import imageHandler from '@/composables/handlers/ImageHandler';
import UseStoreHandler from "@/composables/handlers/UseStoreHandler";
import VariableHandler from "@/composables/handlers/VariableHandler";
import { translate } from "@/composables/translate";
import urlHandler from '@/composables/urlHandler';
import { sleep } from '@/composables/sleep';
import CourseService from '@/services/CourseService';

const authStore = UseStoreHandler.getAuthStore();
const messageStore = UseStoreHandler.getSessionMessageStore();
const storageStore = UseStoreHandler.getStoreContext();

const paymentUrl = ref(false);
const toast = useToast();
const loading = ref(false);
const askConfirmation = ref(false);
const currency = 'R$';
const items = ref([]);
const itemsIds = ref([]);
let isButtonDisabled = ref(false);
let paymentLinkButton = ref(null);
const showSupportButton = ref(false);
const isLoopActive = ref(false);

onMounted(() => {
    items.value = storageStore.get('PURCHASE_ITEM');

    if (!items.value || items.value.length == 0) {
        messageStore.infoMessage(translate('No items selected for purchase.'));
        router.back();
        return;
    }

    if (!authStore.isAuthenticated) {
        messageStore.infoMessage(translate('Log in to your account to continue'));
        urlHandler.redirectAndScroll('/login');
        return;
    }

    handleActions();
});

onUnmounted(() => {
    isLoopActive.value = false;
});

function getConfirmationTitle() {
    return translate('Are you sure you want to proceed?');
}

function getConfirmationText() {
    return translate('If so, you will be redirected to our partner Mercado Pago where you can complete your purchase.');
}

async function handleActions() {
    await canBuyCourses();
    checkIfPaymentWasCompleted();
}

function getTotalAmount() {
    let totalAmount = 0.0;
    for (let item of items.value) {
        totalAmount = totalAmount + parseFloat(item.price);
    }
    return `${currency} ${totalAmount.toFixed(2)}`;
}

function getItemsIds() {
    itemsIds.value = [];

    for (let item of items.value) {
        if (!itemsIds.value.includes(item.id)) {
            itemsIds.value.push(item.id);
        }
    }
}

async function canBuyCourses() {
    getItemsIds();

    const response = await PaymentService.getPendingPayments();

    let areEqual = false;
    for (let payment of response) {
        const courseIds = payment.course_ids;

        areEqual = VariableHandler.areArraysEqual(courseIds, itemsIds.value);
        if (areEqual) {
            paymentLinkButton.value = payment.payment_link
            break;
        }
    }

    isButtonDisabled.value = areEqual;
    return areEqual;
}

/**
 * Adds a border to cart items
 */
function handleBorder(index = 0) {
    return (items.value.length - 1) != index ? 'border-b border-iron' : '';
}

function checkConfirmationResponse(value) {
    if ([null, undefined].includes(value))
        return;
    const confirmation = value.confirmation;
    askConfirmation.value = false;
    if (confirmation) {
        generatePaymentLinkAndRedirect();
    }
}

const payment = ref(null);
const courseUuids = ref([]);
async function generatePaymentLinkAndRedirect() {
    courseUuids.value = [];

    for (let course of items.value) {
        if (!courseUuids.value.includes(course.uuid)) {
            courseUuids.value.push(course.uuid);
        }
    }

    loading.value = true;
    const response = await PaymentService.generatePaymentLink(courseUuids.value);
    loading.value = false;

    if (!response.success()) {
        const responseDataType = response.getContent().type || null;
        const invalidCourses = response.getContent().courses || '';
        if (responseDataType && responseDataType == 'ALREADY_HAVE_COURSE') {
            showToast(toast, translate('you already have bought some of these courses, please check'), 'error');
            showToast(toast, invalidCourses, 'error');
            return;
        }

        showToast(toast, response.getContent().message, 'error');
        showSupportButton.value = true;
        return;
    }

    payment.value = response.getContent();
    const generatedPaymentUrl = payment.value.direction;

    paymentUrl.value = generatedPaymentUrl;
    PaymentService.openPaymentLink(generatedPaymentUrl);
    await canBuyCourses();
}

async function openConfirmationModal() {
    const areEqual = await canBuyCourses();

    if (areEqual) {
        showToast(toast, translate('you already have a pending payment for this course(s). Check your pending payments clicking in the button bellow.', 'ucfirst'), 'info');
        return;
    }

    askConfirmation.value = true;
}

function goToPaymentsView() {
    urlHandler.redirectAndScroll('/dashboard/payment_history', { status: 'pending' });
    return;
}

/**
 * Loops for 30 minutes awaiting for payment aproval
 */
async function checkIfPaymentWasCompleted() {
    isLoopActive.value = true;

    // 30 tentativas com intervalos de 30 segundos de janela de espera, totalizando 15 minutos de espera máxima.
    const maxAttempts = 30; 
    const delayBetweenAttempts = 30000;

    for (let i = 0; i < maxAttempts; i++) {
        if (!isLoopActive.value) {
            break; 
        }

        if (i == 0) {
            await sleep(1000);

        } else {
            await sleep(delayBetweenAttempts);
        }
        
        let uuid = courseUuids.value.length == 1 ? courseUuids.value[0] : null;
        if (!uuid) {
            uuid = items.value.length == 1 ? items.value[0].uuid : null;
        }
        
        const redirectUrl = uuid ? `/dashboard/course/${uuid}` : `/dashboard/`;
        if (!uuid) {
            continue;
        }

        try {
            if (!isLoopActive.value) break;

            const isPayed = await CourseService.isMyCourse(uuid);
            
            if (isPayed) {
                isLoopActive.value = false;
                showToast(toast, translate('payment received', 'ucfirst'));
                urlHandler.redirectAndScroll(redirectUrl);
                break;
            }
        } catch (error) {
            console.error("Error verifying payment status:", error);
        }
    }
}
</script>